<template>
  <div
    :class="[
      'mb-2 flex flex-col-reverse md:flex-row justify-between md:items-end',
      route.name === 'checkout-seats' ? 'mt-1 gap-2 md:mb-4 md:mt-10 md:gap-10' : 'md:mt-10 gap-10',
      { 'md:mb-10': route.name != 'checkout-payment' && route.name !== 'checkout-seats' },
    ]"
  >
    <div class="flex-1 md:max-w-2xl animate-appear">
      <BaseButton
        v-if="!(isEmbed() && $route.name == 'checkout-tickets') && $route.name != 'checkout-payment'"
        :class="['flex gap-1 items-center text-xs text-text hover:underline cursor-pointer mb-4 md:mb-0', $route.name !== 'checkout-seats' ? 'mt-4 md:mt-0' : '']"
        size="sm"
      color="default"
        @click="handleBack"
      >
        <DropdownIcon class="w-3 h-3 fill-current rotate-90" />
        {{ backLabel ?? $t('common.back') }}
      </BaseButton>

      <h1 :class="['h2 mt-2', route.name === 'checkout-seats' ? 'hidden md:block' : '']">{{ title }}</h1>

      <div v-if="activeExpire > 0 && route.name == 'checkout-payment'" class="items-center gap-1 font-semibold text-sm hidden md:flex w-full justify-end">
        <StopwatchIcon class="fill-primary w-6 h-6" />
        <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
      </div>

      <slot />

      <CheckoutCodeInput
        v-if="hasDiscountCode"
        class="md:hidden"
        @on-change="onCodeChange"
        :hasDiscountCode="hasDiscountCode && checkoutStore.event.has_vouchercodes == 1"
      />
    </div>

    <div
      class="md:w-120 md:max-w-full flex -mx-4 md:mx-0 md:justify-end"
    >
      <button
        type="button"
        @click="toggleVouchercodeInput"
        v-if="hasDiscountCode && checkoutStore.event.has_vouchercodes == 1"
        class="hidden md:flex mr-auto border border-surface-grey px-4 py-1 cursor-pointer rounded-full text-sm font-semibold hover:bg-surface-grey-light transition-colors"
      >
        {{ checkoutStore.event.voucher_code_add_description }}
      </button>
      <button
        type="button"
        @click="toggleGiftcardInput"
        v-if="hasGiftCard"
        class="hidden md:flex mr-auto border border-surface-grey px-4 py-1 cursor-pointer rounded-full text-sm font-semibold hover:bg-surface-grey-light transition-colors"
      >
        {{ 'Indløs gavekort' }}
      </button>

      <div
        ref="countdownRef"
        v-if="activeExpire > 0 && route.name != 'checkout-payment' && route.name !== 'checkout-seats' && route.name !== 'doorsale-seats'"
        class="items-center gap-1 font-semibold text-sm hidden md:flex"
      >
        <StopwatchIcon class="fill-primary w-6 h-6" />
        <BaseCountdown :date="activeExpire" /> {{ $t('checkout.countdown') }}
      </div>
      <div
        v-else-if="(route.name === 'checkout-seats' || route.name === 'doorsale-seats') && seatsRemaining > 0"
        class="items-center gap-1 font-semibold text-sm hidden md:flex"
      >
        <SeatInfoIcon class="fill-primary w-6 h-6" />
        {{ seatsRemaining === 1 ? $t('checkout.seats_remaining_one') : $t('checkout.seats_remaining', { count: seatsRemaining }) }}
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watchEffect } from 'vue';
  import { useRoute } from 'vue-router';

  import DropdownIcon from '@/components/icons/DropdownIcon.vue';
  import SeatInfoIcon from '@/components/icons/SeatInfoIcon.vue';
  import StopwatchIcon from '@/components/icons/StopwatchIcon.vue';
  import BaseCountdown from '@/components/ui/BaseCountdown.vue';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useSeatmapStore } from '@/stores/seatmapStore';
  import type { CodeItem } from '@/types';
  import { useCheckoutNavigation } from '@/utils/checkoutNavigation';
  import { isEmbed } from '@/utils/isEmbed';

  import BaseButton from '../ui/BaseButton.vue';
  import CheckoutCodeInput from './CheckoutCodeInput.vue';

  const props = defineProps<{
    title: string;
    backLabel?: string;
    hasDiscountCode?: boolean;
    hasGiftCard?: boolean;
    codeInput?: InstanceType<typeof CheckoutCodeInput> | null;
    onCodeChange?: (codes: CodeItem[]) => void;
    onBack?: () => void;
  }>();

  const cart = useCartStore();
  const checkoutStore = useCheckoutStore();
  const seatmapStore = useSeatmapStore();
  const route = useRoute();
  const { navigateBack } = useCheckoutNavigation();

  const emit = defineEmits<{ 'update:countdownFixed': [value: boolean] }>();

  const countdownRef = ref<HTMLElement | null>(null);
  const countdownFixed = ref(false);

  watchEffect((onCleanup) => {
    const el = countdownRef.value;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        countdownFixed.value = !entry.isIntersecting;
        emit('update:countdownFixed', !entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-84px 0px 0px 0px' }
    );
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  });

  const activeExpire = computed(() =>
    route.name === 'checkout-payment' ? cart.paymentFailedExpire : cart.cartExpire
  );

  const seatsRemaining = computed(() => {
    const item = seatmapStore.currentQueueItem;
    if (!item) return 0;
    const seatsOurs = seatmapStore.seatStates[item.name]?.seatsOurs ?? [];
    return item.count - seatsOurs.length;
  });

  function toggleVouchercodeInput() {
    if (props.codeInput) {
      props.codeInput.toggleVouchercodeInput();
    }
  }

  function handleBack() {
    if (props.onBack) {
      props.onBack();
      return;
    }
    const currentRouteName = route.name?.toString() ?? '';
    navigateBack(currentRouteName);
  }

  function toggleGiftcardInput() {
    if (props.codeInput) {
      props.codeInput.toggleGiftcardInput();
    }
  }
</script>
